import { DateTime } from "luxon";

import { SpendTransaction } from "lib/hooks/api/spend/useSpendTransactions";
import { TopSpendBuyer } from "lib/hooks/api/spend/useTopSpendBuyers";
import { TopSpendSupplier } from "lib/hooks/api/spend/useTopSpendSuppliers";
import { ALL_COMPETITORS_TOKEN, ALL_PARTNERS_TOKEN } from "lib/types/models";
import { SpendDataFilters, SpendView } from "./hooks/useSpendUrlState";

// setting the actual from and to values as these are required for the backend and the date range component doesn't set these from defaults
// Snapped to whole months so spend-data's monthly rollup serves them directly.
export const DEFAULT_DATE_RANGE = {
  relativeFrom: "P-3Y",
  from: DateTime.now().minus({ years: 3 }).startOf("month").toISO() || undefined,
  relativeTo: "PT0S",
  to: DateTime.now().endOf("month").startOf("day").toISO() || undefined,
};

export type SpendPreset = {
  title: string;
  description: string;
  filters: SpendDataFilters;
  /** The view that answers the template's question. */
  view: Partial<SpendView>;
  /** Which saved signal the preset filters on; with none saved it can't return anything. */
  requires: "competitors" | "partners";
};

/**
 * The curated starting points. Each applies DEFAULT_DATE_RANGE alongside its supplier
 * filter so data loads immediately, and opens the ranking that answers its question.
 * The user can change either afterwards.
 *
 * The supplier filter is the signal token rather than the guids behind it, so a template
 * used today still covers a competitor saved tomorrow, and picking one before the saved
 * suppliers have loaded cannot freeze an empty filter into the URL.
 */
export const SPEND_PRESETS: SpendPreset[] = [
  {
    title: "Buyer & Competitor relationships",
    description: "Which buyers are working with my competitors?",
    filters: { supplierGuids: [ALL_COMPETITORS_TOKEN], dateRange: DEFAULT_DATE_RANGE },
    view: { mode: "trends", trendsGroupBy: "buyers" },
    requires: "competitors",
  },
  {
    title: "Competitor rankings",
    description: "Which of my competitors are earning the most in the public sector?",
    filters: { supplierGuids: [ALL_COMPETITORS_TOKEN], dateRange: DEFAULT_DATE_RANGE },
    view: { mode: "trends", trendsGroupBy: "suppliers" },
    requires: "competitors",
  },
  {
    title: "Buyer & Partner relationships",
    description: "Which buyers are working with my partners?",
    filters: { supplierGuids: [ALL_PARTNERS_TOKEN], dateRange: DEFAULT_DATE_RANGE },
    view: { mode: "trends", trendsGroupBy: "buyers" },
    requires: "partners",
  },
  {
    title: "Partner rankings",
    description: "Which of my partners are earning the most in the public sector?",
    filters: { supplierGuids: [ALL_PARTNERS_TOKEN], dateRange: DEFAULT_DATE_RANGE },
    view: { mode: "trends", trendsGroupBy: "suppliers" },
    requires: "partners",
  },
];

/** A transaction as the table draws it: its file's descriptive fields keyed for columns. */
export type TransactionTableRow = SpendTransaction & {
  descriptiveColumns?: Record<string, string>;
};

/** A row of a Trends ranking: one buyer or one supplier with its totals. */
export type SpendRankingRow = TopSpendBuyer | TopSpendSupplier;
